import React from 'react'
import PropTypes from 'prop-types'
import I18n from 'translations/i18n'
import { useForm, useFormState } from 'react-final-form'
import { useSelector } from 'react-redux'
import { Flex, Spacer, Button, Tooltip } from '@jisr-hr/design-system'
import { requestsWithCancellationFlow } from 'components/global/templates/RequestDrawer/helper'
import { Toastr } from 'components/global/Toastr'
import Auth from 'components/Auth'
import { useRequest } from '../RequestProvider'
import ResumptionActions from './ResumptionActions'
import { ReactComponent as CheckIcon } from '../icons/check.svg'
import { ReactComponent as CloseIcon } from '../icons/close.svg'
import Options from './Options'
import AddComment from './AddComment'
import TaskActions from './TaskActions'
import Styles from './style.module.css'

const Footer = ({ readOnly, handlePrint }) => {
  const {
    vars: { rejectComment, request: req } = {},
    drawerToggle,
    isOtherRequest,
    canApprove,
    canCancel,
    onCancel,
    onSuperAdminCancel,
    requestType,
  } = useRequest()
  const { id: employee_id } = useSelector(({ auth }) => auth.employee)
  const is_super_admin = useSelector(({ auth }) => auth.employee?.is_super_admin) ?? false
  const { request: details } = useSelector(({ requestDetails }) => requestDetails)

  const { submit } = useForm()
  const { invalid, submitting } = useFormState()
  const { disableSubmit, onReject } = useRequest()
  const [addRejectComment, setAddRejectComment] = React.useState()
  const [status, setStatus] = React.useState('reject')
  const [isCopied, setIsCopied] = React.useState(false)

  const request = {
    ...req,
    ...req?.request,
    ...details,
  }

  const isApproved = request?.status?.toLowerCase() === 'approved'
  const isPending = request?.status?.toLowerCase() === 'pending'
  const showCancellationFlow = requestsWithCancellationFlow.includes(request?.request_type)

  const handleReject = () => {
    setAddRejectComment(true)
    setStatus('reject')
  }

  const handleCancelRequest = () => {
    if (showCancellationFlow && isApproved) {
      setAddRejectComment(true)
      setStatus('cancel')
    } else {
      onCancel()
    }
  }

  const handleCancelImmediately = () => {
    onSuperAdminCancel()
  }

  const showResumptionActions =
    isApproved &&
    request?.leave_resumption_keys &&
    (request?.leave_resumption_keys?.confirm || request?.leave_resumption_keys?.early_return)

  const askCancelAuthorize =
    showCancellationFlow &&
    isApproved &&
    ((is_super_admin && !showResumptionActions) || [request?.employee?.id].includes(employee_id))

  const canAdminCancel = !showResumptionActions && is_super_admin && (isApproved || isPending)
  const canEmployeeCancel = canCancel && !is_super_admin && isPending

  const copyIcon = isCopied ? 'check' : 'copy-01'

  const handleCancelation = () => {
    if (canCancel && canAdminCancel) handleCancelImmediately()
    else if (canEmployeeCancel) handleCancelRequest()
  }

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/#/?employee_id=${request?.employee?.id}&request_type=${requestType}&sequential_id=${request.sequential_id}`
    try {
      await navigator.clipboard.writeText(link)
      setIsCopied(true)

      const timeout = setTimeout(() => {
        setIsCopied(false)
        clearTimeout(timeout)
      }, 5000)
    } catch {
      Toastr.error(I18n.t('something_went_wrong_please_try_again'))
      setIsCopied(false)
    }
  }

  React.useEffect(() => {
    setAddRejectComment(rejectComment)
  }, [])

  if (addRejectComment) {
    return (
      <AddComment
        onSubmit={(comment) => (status === 'reject' ? onReject(comment) : onCancel(comment))}
        onCancel={() => setAddRejectComment(false)}
      />
    )
  }

  if (request?.isTask) {
    return <TaskActions handlePrint={handlePrint} />
  }

  return (
    <Flex style={{ flexDirection: 'row', width: '100%', gap: 6 }}>
      {canApprove && (
        <Flex>
          <Button
            label={readOnly ? I18n.t('approve') : I18n.t(isOtherRequest ? 'next' : 'submit')}
            size="small"
            variant="primary"
            startIcon={readOnly && <CheckIcon />}
            disabled={invalid || disableSubmit || submitting}
            onClick={submit}
            data-testid="pending-request-details-approve"
          />
          <Spacer width={8} />
          <Button
            label={readOnly ? I18n.t('reject') : I18n.t('cancel')}
            size="small"
            variant="secondary"
            startIcon={readOnly && <CloseIcon />}
            onClick={readOnly ? handleReject : drawerToggle}
            data-testid="pending-request-details-reject"
          />
          <Spacer width={8} />
        </Flex>
      )}
      {readOnly && (
        <>
          <Auth isAuthorized={showResumptionActions}>
            <ResumptionActions request={request} />
            <Auth isAuthorized={is_super_admin}>
              <Options
                list={[
                  {
                    title: 'ask_for_cancellation',
                    description: 'send_cancellation_request',
                    onClick: handleCancelRequest,
                  },
                  {
                    title: 'cancel_immediately',
                    description: 'cancel_request_immediately',
                    onClick: handleCancelImmediately,
                  },
                  {
                    title: 'print',
                    onClick: handlePrint,
                    iconName: 'printer',
                  },
                  {
                    title: 'copy_request_link',
                    onClick: handleCopyLink,
                    iconName: copyIcon,
                  },
                ]}
              />
            </Auth>
            <Auth isAuthorized={!is_super_admin}>
              <Options
                list={[
                  {
                    title: 'print',
                    onClick: handlePrint,
                    iconName: 'printer',
                  },
                  {
                    title: 'copy_request_link',
                    onClick: handleCopyLink,
                    iconName: copyIcon,
                  },
                ]}
              />
            </Auth>
          </Auth>

          <Auth isAuthorized={askCancelAuthorize}>
            <Tooltip
              title={I18n.t('send_cancellation_request')}
              placement="top"
            >
              <Button
                label={I18n.t('ask_for_cancellation')}
                size="small"
                variant="secondary"
                onClick={handleCancelRequest}
              />
            </Tooltip>
          </Auth>

          <Auth isAuthorized={(canCancel && canAdminCancel) || canEmployeeCancel}>
            <Tooltip
              title={I18n.t('cancel_request_immediately')}
              placement="top"
            >
              <Button
                label={I18n.t('cancel_immediately')}
                className={Styles.cancelRequestButton}
                size="small"
                variant="secondary"
                onClick={handleCancelation}
                data-testid="cancel-immediately-btn"
              />
            </Tooltip>
          </Auth>

          {!showResumptionActions && (
            <Options
              list={[
                {
                  title: 'print',
                  onClick: handlePrint,
                  iconName: 'printer',
                },
                {
                  title: 'copy_request_link',
                  onClick: handleCopyLink,
                  iconName: copyIcon,
                },
              ]}
            />
          )}
        </>
      )}
    </Flex>
  )
}

Footer.propTypes = {
  readOnly: PropTypes.bool,
  handlePrint: PropTypes.func,
}

export default Footer
